import { t } from 'i18next'
import { useFormContext } from 'react-hook-form'

import { Render } from '@/components/render-if'
import { Button } from '@/components/ui'
import { useMutationAtualizarCliente } from '@/features/clientes/hooks/use-atualizar-cliente'
import { useSuspenseNacionalidade } from '@/features/clientes/hooks/use-suspense-nacionalidade'
import { useSuspenseProfissoes } from '@/features/clientes/hooks/use-suspense-profissoes'
import {
  CadastrarCompradorButton,
  CadastroCompradoresEmpty,
  FluxosCadastroComprador,
  ListaCompradores,
} from '@/features/proposta/components/etapa-comprador'
import { useEtapaCompradoresLista, useStepValidationStrategy } from '@/features/proposta/hooks'
import {
  StepPropostaContent,
  StepPropostaFooter,
  StepPropostaHeader,
  StepPropostaHeaderAction,
  StepPropostaHeaderContent,
  StepPropostaHeaderDescription,
  StepPropostaHeaderTitle,
  StepPropostaRoot,
} from '@/features/proposta/layouts'
import type { TPropostaSchemaInput } from '@/features/proposta/schemas'
import { useMutationCadastrarCliente } from '@/hooks/use-cadastrar-clientes'
import { showToast } from '@/infra/sonner'

export const EtapaComprador = () => {
  const {
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useFormContext<TPropostaSchemaInput>()
  const { validateStep } = useStepValidationStrategy(trigger)

  const currentStep = watch('step')

  const { data: nacionalidades } = useSuspenseNacionalidade()
  const { data: profissoes } = useSuspenseProfissoes()
  const { mutateAsync: cadastrarCliente } = useMutationCadastrarCliente()
  const { mutateAsync: atualizarCliente } = useMutationAtualizarCliente()

  const {
    clientes,
    procuradores,
    clientesJuridicos,
    compradoresComum,
    emailsJaVinculados,
    isEmpty,
    opcoesCadastro,
    clienteAtendimento,
    flowKey,
    mode,
    clienteToEdit,
    openCreate,
    handleEdit,
    handleCloseStepsComprador,
    handleSubmit,
    handleOnExcluirComprador,
    obterProcuradoresDoComprador,
  } = useEtapaCompradoresLista({ nacionalidades, profissoes, clienteMutations: { cadastrarCliente, atualizarCliente } })

  const handlePrevStep = () => setValue('step', currentStep - 1)
  const handleNextStep = async () => {
    const isValid = await validateStep('CadastroCompradoresStep')

    if (!isValid) {
      if (errors.cadastroCompradores?.message) {
        showToast({
          message: t('proposta:cadastro_compradores.erro.obrigatorio'),
          variant: 'error',
        })
      }
      return
    }
    setValue('step', currentStep + 1)
  }

  if (flowKey) {
    return (
      <FluxosCadastroComprador
        flowKey={flowKey}
        mode={mode}
        nacionalidades={nacionalidades}
        profissoes={profissoes}
        clientes={clientes}
        compradores={compradoresComum}
        procuradores={procuradores}
        clientesJuridicos={clientesJuridicos}
        clienteToEdit={clienteToEdit}
        emailsJaVinculados={emailsJaVinculados}
        clienteAtendimento={clienteAtendimento}
        onCancel={handleCloseStepsComprador}
        onSubmit={handleSubmit}
      />
    )
  }

  return (
    <StepPropostaRoot>
      <StepPropostaHeader>
        <StepPropostaHeaderContent>
          <StepPropostaHeaderTitle className="text-left">
            {t('proposta:cadastro_compradores.titulo')}
          </StepPropostaHeaderTitle>
          <StepPropostaHeaderDescription>
            {t(
              isEmpty
                ? 'proposta:cadastro_compradores.subtitulo_inicial'
                : 'proposta:cadastro_compradores.subtitulo_adicionar',
            )}
          </StepPropostaHeaderDescription>
        </StepPropostaHeaderContent>
        <StepPropostaHeaderAction className="hidden md:block">
          <CadastrarCompradorButton
            isEmpty={isEmpty}
            opcoesCadastro={opcoesCadastro}
            onSelect={openCreate}
            testId="btn-cadastrar-comprador"
          />
        </StepPropostaHeaderAction>
      </StepPropostaHeader>
      <StepPropostaContent>
        <div className="-mt-2 w-full md:hidden">
          <CadastrarCompradorButton
            isEmpty={isEmpty}
            opcoesCadastro={opcoesCadastro}
            onSelect={openCreate}
            testId="btn-cadastrar-comprador-mobile"
          />
        </div>
        <Render.If condition={!isEmpty} fallback={<CadastroCompradoresEmpty />}>
          {() => (
            <ListaCompradores
              compradores={compradoresComum}
              onExcluir={handleOnExcluirComprador}
              onEdit={handleEdit}
              obterProcuradoresDoComprador={obterProcuradoresDoComprador}
            />
          )}
        </Render.If>
      </StepPropostaContent>
      <StepPropostaFooter>
        <Button className="font-nunito h-12 w-full md:w-auto" variant="outline" onClick={handlePrevStep}>
          {t('proposta:etapa_anterior')}
        </Button>
        <Button className="font-nunito h-12 w-full md:w-auto" onClick={handleNextStep}>
          {t('proposta:proxima_etapa')}
        </Button>
      </StepPropostaFooter>
    </StepPropostaRoot>
  )
}
